<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import logger from 'shared/helpers/logger';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import { emitter } from 'shared/helpers/mitt';
import { PIPELINE_EVENTS } from 'dashboard/helper/pipelineCardCable';
import PipelineCardDetails from './PipelineCardDetails.vue';

const props = defineProps({
  itemId: {
    type: [Number, String],
    required: true,
  },
  forceReload: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['back', 'itemUpdated']);

const store = useStore();
const { t } = useI18n();

const item = ref(null);
const isLoading = ref(true);
const loadError = ref(false);
let fetchRequestId = 0;
let fetchController = null;
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const normalizeContextId = value => String(value ?? '').trim();
const currentFetchContext = () => ({
  accountId: normalizeContextId(currentAccountId.value),
  itemId: normalizeContextId(props.itemId),
});
const fetchContextMatches = (context, requestId) => {
  const currentContext = currentFetchContext();
  return (
    requestId === fetchRequestId &&
    currentContext.accountId === context.accountId &&
    currentContext.itemId === context.itemId
  );
};
const cancelItemFetch = () => {
  fetchRequestId += 1;
  fetchController?.abort();
  fetchController = null;
};

// `silent` skips the full-screen loading spinner. Used by handleItemUpdated so
// that in-place updates (status change, notes, assignee…) reconcile in the
// background WITHOUT blanking the whole card detail on every mutation. The
// initial load / item switch (the watch below) keeps the spinner.
const fetchItemDetails = async ({ silent = false } = {}) => {
  const context = currentFetchContext();
  if (!context.accountId || !context.itemId) {
    if (!silent) {
      isLoading.value = false;
      loadError.value = true;
    }
    return;
  }
  cancelItemFetch();
  const requestId = fetchRequestId;
  const controller = new AbortController();
  fetchController = controller;
  try {
    if (!silent) {
      isLoading.value = true;
      loadError.value = false;
    }
    const { data } = await PipelineCardsAPI.getCard(context.itemId, {
      accountId: context.accountId,
      signal: controller.signal,
    });
    if (controller.signal.aborted || !fetchContextMatches(context, requestId))
      return;
    item.value = data;
  } catch (error) {
    if (controller.signal.aborted || !fetchContextMatches(context, requestId))
      return;
    logger.error(
      '[DEBUG PipelineCardDetailView] fetchItemDetails - Erro:',
      error
    );
    if (!silent) loadError.value = true;
  } finally {
    if (!silent && fetchContextMatches(context, requestId)) {
      isLoading.value = false;
    }
    if (fetchController === controller) fetchController = null;
  }
};

// Watch após a definição da função
watch(
  [currentAccountId, () => props.itemId, () => props.forceReload],
  () => {
    // `flush: sync` prevents one tenant/card from remaining visible during the
    // render gap before the next request starts.
    cancelItemFetch();
    item.value = null;
    loadError.value = false;
    isLoading.value = true;
    logger.debug(
      '[DEBUG PipelineCardDetailView] Estados limpos antes de buscar novo item'
    );

    logger.debug('[DEBUG PipelineCardDetailView] Fetching new item details...');
    fetchItemDetails();
  },
  { immediate: true, flush: 'sync' }
);

// === Realtime do card aberto (fase-57 RT-P1-7) ===
// O board atualizava em tempo real e o card ABERTO ficava congelado: as
// mutations de realtime mexem em `pipelineCards.items`, e esta tela mantém a
// própria cópia buscada por getCard. Os eventos do cable já eram emitidos no
// mitt, mas nenhum componente os consumia. Aqui reaproveitamos o reconcile
// silencioso que já existe (sem spinner, sem piscar) em vez de traduzir o
// payload magro do broadcast para o formato desta tela.
const REMOTE_RECONCILE_DEBOUNCE_MS = 400;
let remoteReconcileTimer = null;

const isEventForOpenCard = data => {
  const eventCardId = data?.card?.id;
  if (eventCardId == null) return false;
  return String(eventCardId) === normalizeContextId(props.itemId);
};

const scheduleRemoteReconcile = data => {
  if (!isEventForOpenCard(data)) return;
  // Uma sequência de automação dispara vários eventos para o mesmo card em
  // milissegundos (etapa + responsável + updated); um refetch resolve todos.
  if (remoteReconcileTimer) clearTimeout(remoteReconcileTimer);
  remoteReconcileTimer = setTimeout(() => {
    remoteReconcileTimer = null;
    fetchItemDetails({ silent: true });
  }, REMOTE_RECONCILE_DEBOUNCE_MS);
};

// Trocar as ETAPAS do pipeline remaneja cards órfãos no SERVIDOR sem emitir
// evento de card: sem isto o card aberto continuaria exibindo uma etapa que
// deixou de existir e a próxima ação dele daria 422 (achado do cross-review).
const handleRemotePipelineUpdated = data => {
  const changedFields = data?.changed_fields || [];
  if (!changedFields.includes('stages')) return;
  if (!item.value) return;
  const eventPipelineId = data?.pipeline?.id;
  if (
    eventPipelineId != null &&
    String(eventPipelineId) !== String(item.value.pipeline_id)
  ) {
    return;
  }

  scheduleRemoteReconcile({ card: { id: props.itemId } });
};

const handleRemoteCardDeleted = data => {
  if (!isEventForOpenCard(data)) return;
  // O card que está na tela deixou de existir — insistir nele só levaria a um
  // 404 na próxima ação.
  emit('itemUpdated');
  emit('back');
};

const REMOTE_RECONCILE_EVENTS = [
  PIPELINE_EVENTS.CARD_UPDATED,
  PIPELINE_EVENTS.CARD_STAGE_CHANGED,
  PIPELINE_EVENTS.CARD_WON,
  PIPELINE_EVENTS.CARD_LOST,
  PIPELINE_EVENTS.CARD_OWNER_CHANGED,
  // Nota/tarefa criada por automação não muda o card, mas muda a timeline que
  // esta tela exibe (item_details.history/activities).
  PIPELINE_EVENTS.ACTIVITY_CHANGED,
];

onMounted(() => {
  REMOTE_RECONCILE_EVENTS.forEach(event =>
    emitter.on(event, scheduleRemoteReconcile)
  );
  emitter.on(PIPELINE_EVENTS.CARD_DELETED, handleRemoteCardDeleted);
  emitter.on(PIPELINE_EVENTS.PIPELINE_UPDATED, handleRemotePipelineUpdated);
});

onBeforeUnmount(() => {
  REMOTE_RECONCILE_EVENTS.forEach(event =>
    emitter.off(event, scheduleRemoteReconcile)
  );
  emitter.off(PIPELINE_EVENTS.CARD_DELETED, handleRemoteCardDeleted);
  emitter.off(PIPELINE_EVENTS.PIPELINE_UPDATED, handleRemotePipelineUpdated);
  if (remoteReconcileTimer) clearTimeout(remoteReconcileTimer);
  remoteReconcileTimer = null;
});

onBeforeUnmount(cancelItemFetch);

const handleBack = () => {
  logger.debug('[DEBUG PipelineCardDetailView] handleBack - Iniciando...');
  logger.debug(
    '[DEBUG PipelineCardDetailView] handleBack - Item atual:',
    item.value
  );
  // Limpar estados
  item.value = null;

  logger.debug('[DEBUG PipelineCardDetailView] handleBack - Estados limpos');
  emit('back');
};

const handleItemUpdated = async patch => {
  // `patch` is the authoritative payload returned by the deal-status endpoints
  // (mark_won / mark_lost / reopen): { id, status, pipeline_stage, won_at,
  // lost_at, lost_reason, won_value, ... }. Apply it to BOTH the open card and
  // the board store synchronously so neither shows a stale status while the
  // background refetch is in flight — this is what fixes the long-standing
  // "muda o status com o card aberto e não persiste / volta ao anterior" bug.
  if (patch && typeof patch === 'object' && patch.id != null) {
    const patchContext = currentFetchContext();
    if (
      patchContext.accountId &&
      patchContext.itemId &&
      String(patch.id) === patchContext.itemId &&
      String(item.value?.id) === patchContext.itemId
    ) {
      item.value = { ...item.value, ...patch };
      store.commit('pipelineCards/UPDATE_PIPELINE_CARD_ITEM', patch);
    }
  }
  emit('itemUpdated');
  // Silent reconcile (activities, derived fields). No spinner: the card stays
  // visible and already shows the patched status.
  await fetchItemDetails({ silent: true });
};

const handleDetailsDeleted = () => {
  emit('itemUpdated');
  emit('back');
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-solid-2 overflow-hidden">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex-1 flex justify-center items-center py-12">
      <span
        class="w-8 h-8 border-2 border-t-n-brand border-r-n-brand border-b-transparent border-l-transparent rounded-full motion-safe:animate-spin"
        aria-hidden="true"
      />
      <span class="sr-only">{{ t('PIPELINE.LOADING_ITEMS') }}</span>
    </div>

    <div
      v-else-if="loadError"
      class="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-12 text-center"
      role="alert"
    >
      <span
        class="i-lucide-circle-alert size-8 text-n-ruby-10"
        aria-hidden="true"
      />
      <p class="m-0 text-sm text-n-slate-11">
        {{ t('PIPELINE.MESSAGES.ITEM_LOAD_ERROR') }}
      </p>
      <button
        type="button"
        class="inline-flex min-h-11 items-center rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
        @click="fetchItemDetails()"
      >
        {{ t('PIPELINE.MESSAGES.RETRY') }}
      </button>
    </div>

    <!-- Content -->
    <div v-else class="flex-1 min-h-0 overflow-hidden">
      <!-- Visualização inline (all editing is done inline) -->
      <PipelineCardDetails
        v-if="item"
        :item="item"
        @close="handleBack"
        @item-updated="handleItemUpdated"
        @deleted="handleDetailsDeleted"
      />
    </div>
  </div>
</template>
